/**
 * Canonical Tool Renderer Registry
 * 
 * Registry for tool-specific rendering in CanonicalToolCard.
 * Enables extensible rendering for different tool types (web_search, doc_write, etc.)
 * 
 * Similar to CanonicalCardRegistry but specifically for tools.
 */

import React from 'react';
import { ToolExecutionState } from '../../types/CanonicalMessage';
import { createFileLogger } from '../../../../../utils/logger';

const log = createFileLogger('CANONICAL_TOOL_RENDERER_REGISTRY', { enabled: false });

/**
 * Context provided to tool renderers
 */
export interface CanonicalToolRenderContext {
  toolId: string;
  /** Anthropic/provider block ID (toolu_*, call_*, …). Equals ctx.toolCallId inside the tool handler.
   *  Use this as the correlationId for waitForHumanInput — NOT toolId, which is a frontend-generated
   *  sequential ID (headless_toolName_N) that differs from the provider block ID. */
  providerToolCallId?: string;
  toolName: string;
  input: Record<string, any>;
  execution: ToolExecutionState;
  threadId: string;
  entityName?: string;
  resolvedEntity?: any; // Entity object if resolved
  getEntityIcon?: (entity: any) => React.ReactNode; // Function to get entity icon
}

/**
 * Tool renderer interface
 * Different tools can customize their rendering for each state
 */
export interface CanonicalToolRenderer {
  // --- Header Customization ---
  
  // Icon to display in header (React component or function returning ReactNode)
  // If not provided, uses PencilSimple as fallback
  icon?: React.ComponentType<any> | ((context: CanonicalToolRenderContext) => React.ReactNode);
  
  // Display name for the tool (used when entity context is not available)
  // Can be a string or function that generates the name dynamically
  displayName?: string | ((context: CanonicalToolRenderContext) => string);
  
  // Action verb for the tool (e.g., "Reading"/"Read", "Writing"/"Wrote")
  // Returns present tense when isExecuting=true, past tense when false
  getVerb?: (isExecuting: boolean, context: CanonicalToolRenderContext) => string;
  
  // Custom header message (appears after entity name in header)
  // Allows dynamic messages like "Found 5 sources", "3 results", etc.
  // Return null to show no message and skip execution result fallback; undefined to use default fallback.
  getHeaderMessage?: (context: CanonicalToolRenderContext) => string | null | undefined;
  
  // Whether to resolve entity (fetch entity name/icon from ID)
  // false = skip entity resolution, use displayName instead
  // Default: true
  shouldResolveEntity?: boolean;
  
  // Whether to show the entity icon INSTEAD of the tool icon in the header
  // true = use entity icon (e.g., doc writes - show what's being written to)
  // false = use tool icon (e.g., doc reads - show the action)
  // Default: false (show tool icon)
  useEntityIcon?: boolean;
  
  // Whether clicking on the completed tool card opens/navigates to the entity
  // Can be boolean or function for dynamic behavior
  // Default: true if execution has documentId
  isNavigable?: boolean | ((context: CanonicalToolRenderContext) => boolean);
  
  // Fallback name to use when entity resolution fails
  // Shows when no entity name is found in input or store
  // Can be a string or function for dynamic behavior
  // Default: "Entity" (if not specified)
  fallbackEntityName?: string | ((context: CanonicalToolRenderContext) => string);
  
  // --- Layout Customization ---
  
  // Compact/inline rendering (alternative to full card view)
  // Used for tools that can be displayed more concisely (e.g., doc_read)
  // If provided, this overrides the entire card rendering when completed
  renderCompact?: (context: CanonicalToolRenderContext) => React.ReactNode;

  // When true, renderCompact is used for ALL execution states (queued, executing, completed, error),
  // bypassing the full tool-use-card shell entirely. renderCompact must handle all states itself.
  inlineOnly?: boolean;
  
  // Whether this tool should be expanded by default when completed
  // true = expanded (show results), false = collapsed (click to expand)
  // Default: true for most tools, false for verbose read operations
  defaultExpanded?: boolean;
  
  // --- State Rendering ---
  
  // Render during execution (optional - shows progress/live updates)
  renderExecuting?: (context: CanonicalToolRenderContext) => React.ReactNode;
  
  // Render completed state (optional when silent: true)
  renderCompleted?: (context: CanonicalToolRenderContext) => React.ReactNode;
  
  // Render error state (optional - uses default if not provided)
  renderError?: (context: CanonicalToolRenderContext) => React.ReactNode;
  
  // --- Behavior Flags ---

  // When true, the tool card renders nothing at all (no UI shown during or after execution).
  // Use for internal plumbing tools the user should never see (e.g. search_tools).
  silent?: boolean;

  // Returns true when the tool completed successfully but produced no meaningful results.
  // Causes the card to render in a minimal inline style (no expand button, no body, muted header).
  // Use for tools whose empty result is normal and not worth visual emphasis.
  isEmptyResult?: (context: CanonicalToolRenderContext) => boolean;

  // When true, this tool's entire execution phase is a Human-in-the-Loop (HITL) wait —
  // the tool is blocked in waitForHumanInput(), not actively computing.
  //
  // CanonicalToolCard bypasses the standard executing visual:
  //   - No lighting-effect animation
  //   - No standard header or verb
  //   - renderExecuting() is rendered directly inside a static .hitl-pending card wrapper
  //
  // Always pair with a renderExecuting() that shows the approval UI (confirm card with
  // Send/Cancel or equivalent). Use providerToolCallId (not toolId) as the correlationId.
  //
  // See: tool-runtime/src/agent/humanInputRegistry.ts
  // See: CREATING_TOOLS.md — Pattern 4 (HITL tools)
  humanInputPending?: boolean;

  // Whether this renderer supports streaming
  supportsStreaming?: boolean;
}

interface FallbackRendererEntry {
  predicate: (toolName: string) => boolean;
  renderer: CanonicalToolRenderer;
}

/**
 * Registry for canonical tool renderers
 */
class CanonicalToolRendererRegistry {
  private renderers = new Map<string, CanonicalToolRenderer>();
  private fallbacks: FallbackRendererEntry[] = [];

  /**
   * Register a tool renderer by exact tool name.
   */
  register(toolName: string, renderer: CanonicalToolRenderer): void {
    if (this.renderers.has(toolName)) {
      log.warn(`[CanonicalToolRendererRegistry] Overwriting renderer for: ${toolName}`);
    }
    this.renderers.set(toolName, renderer);
    log.log(`[CanonicalToolRendererRegistry] Registered renderer for: ${toolName}`);
  }

  /**
   * Register a fallback renderer that applies to tool names matching a predicate.
   * Fallbacks are checked in registration order when no exact-name match exists.
   */
  registerFallback(predicate: (toolName: string) => boolean, renderer: CanonicalToolRenderer): void {
    this.fallbacks.push({ predicate, renderer });
  }

  /**
   * Get renderer for a tool — exact match first, then first matching fallback.
   */
  getRenderer(toolName: string): CanonicalToolRenderer | null {
    if (this.renderers.has(toolName)) return this.renderers.get(toolName)!;
    return this.fallbacks.find(f => f.predicate(toolName))?.renderer ?? null;
  }

  /**
   * Check if a renderer exists for the given tool name.
   */
  hasRenderer(toolName: string): boolean {
    if (this.renderers.has(toolName)) return true;
    return this.fallbacks.some(f => f.predicate(toolName));
  }
  
  /**
   * Render executing state for a tool
   */
  renderExecuting(context: CanonicalToolRenderContext): React.ReactNode {
    const renderer = this.renderers.get(context.toolName);
    
    if (!renderer || !renderer.renderExecuting) {
      return null; // Use default rendering in CanonicalToolCard
    }
    
    try {
      return renderer.renderExecuting(context);
    } catch (error) {
      log.error(`[CanonicalToolRendererRegistry] Error in renderExecuting for ${context.toolName}:`, error);
      return null;
    }
  }
  
  /**
   * Render completed state for a tool
   */
  renderCompleted(context: CanonicalToolRenderContext): React.ReactNode {
    const renderer = this.renderers.get(context.toolName);
    
    if (!renderer) {
      return null; // Use default rendering in CanonicalToolCard
    }
    
    try {
      return renderer.renderCompleted?.(context) ?? null;
    } catch (error) {
      log.error(`[CanonicalToolRendererRegistry] Error in renderCompleted for ${context.toolName}:`, error);
      return this.renderError(context, error);
    }
  }
  
  /**
   * Render error state for a tool
   */
  renderError(context: CanonicalToolRenderContext, error?: any): React.ReactNode {
    const renderer = this.renderers.get(context.toolName);
    
    if (renderer && renderer.renderError) {
      try {
        return renderer.renderError(context);
      } catch (err) {
        log.error(`[CanonicalToolRendererRegistry] Error in renderError for ${context.toolName}:`, err);
      }
    }
    
    // Default error rendering
    return React.createElement('div', {
      className: 'tool-render-error'
    }, [
      React.createElement('strong', { key: 'title', className: 'tool-render-error-title' }, 'Rendering Error'),
      React.createElement('div', { key: 'message', className: 'tool-render-error-message' }, 
        error?.message || 'Failed to render tool result')
    ]);
  }
  
  /**
   * Get all registered tool names
   */
  getRegisteredTools(): string[] {
    return Array.from(this.renderers.keys());
  }
  
  /**
   * Clear all renderers (for testing)
   */
  clear(): void {
    this.renderers.clear();
  }
}

// Singleton instance
export const canonicalToolRendererRegistry = new CanonicalToolRendererRegistry();


